<template>
  <div class="page-wrapper">
    <HeaderComponent />

    <main class="main-content">
      <!-- Page Hero -->
      <section class="page-hero">
        <div class="container">
          <div class="hero-content">
            <nav class="breadcrumb-nav">
              <a href="/" class="breadcrumb-link">{{ $t('nav.home') }}</a>
              <span class="breadcrumb-separator">/</span>
              <span class="breadcrumb-current">{{ $t('pages.contact.title') }}</span>
            </nav>
            <h1 class="page-title">{{ $t('pages.contact.title') }}</h1>
            <p class="page-subtitle">We're here to help. Reach out to us through any of the channels below.</p>
          </div>
        </div>
      </section>

      <!-- Quick Contact Cards -->
      <section class="quick-contact-section">
        <div class="container">
          <div class="contact-cards">
            <div class="contact-card">
              <div class="card-icon phone">
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path>
                </svg>
              </div>
              <h3>{{ $t('pages.contact.callTitle') }}</h3>
              <p class="card-description">{{ $t('pages.contact.callBody') }}</p>
              <a :href="'tel:' + (county?.contact || '0700000000')" class="card-link">
                {{ county?.contact || '0700-000-000' }}
              </a>
              <span class="availability">Mon-Fri: 8:00 AM - 5:00 PM</span>
            </div>

            <div class="contact-card">
              <div class="card-icon email">
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path>
                  <polyline points="22,6 12,13 2,6"></polyline>
                </svg>
              </div>
              <h3>{{ $t('pages.contact.emailTitle') }}</h3>
              <p class="card-description">Get a response within 24 hours</p>
              <a :href="'mailto:' + (county?.email || 'info@county.go.ke')" class="card-link">
                {{ county?.email || 'info@county.go.ke' }}
              </a>
              <span class="availability">Response within 24 hours</span>
            </div>

            <div class="contact-card">
              <div class="card-icon location">
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
                  <circle cx="12" cy="10" r="3"></circle>
                </svg>
              </div>
              <h3>{{ $t('pages.contact.visitTitle') }}</h3>
              <p class="card-description">{{ $t('pages.contact.visitBody') }}</p>
              <span class="card-link">{{ county?.address || 'County Headquarters' }}</span>
              <span class="availability">Mon-Fri: 8:00 AM - 5:00 PM</span>
            </div>

            <div class="contact-card">
              <div class="card-icon support">
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"></path>
                </svg>
              </div>
              <h3>{{ $t('pages.contact.chatTitle') }}</h3>
              <p class="card-description">Chat with our support team</p>
              <button class="card-link-btn" @click="openLiveChat">Start Chat</button>
              <span class="availability">Available during office hours</span>
            </div>
          </div>
        </div>
      </section>

      <!-- Main Content -->
      <section class="content-section">
        <div class="container">
          <div class="content-grid">
            <!-- Contact Form -->
            <div class="form-panel">
              <div class="form-card">
                <div class="form-card-header">
                  <h2>Send us a Message</h2>
                  <p>Fill out the form below and we'll get back to you as soon as possible.</p>
                </div>

                <div class="form-card-body">
                  <!-- Success Message -->
                  <div v-if="formSubmitted" class="success-message">
                    <div class="success-icon">
                      <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                        <polyline points="22 4 12 14.01 9 11.01"></polyline>
                      </svg>
                    </div>
                    <h3>Message Sent Successfully!</h3>
                    <p>Thank you for contacting us. We'll respond to your inquiry within 24 hours.</p>
                    <button class="btn btn-secondary" @click="resetForm">Send Another Message</button>
                  </div>

                  <!-- Contact Form -->
                  <form v-else @submit.prevent="submitForm">
                    <div class="form-row">
                      <div class="form-group">
                        <label class="form-label">
                          Full Name <span class="text-danger">*</span>
                        </label>
                        <input
                            type="text"
                            v-model="formData.fullName"
                            class="form-input"
                            placeholder="Enter your full name"
                            required
                        />
                      </div>
                      <div class="form-group">
                        <label class="form-label">
                          Phone Number <span class="text-danger">*</span>
                        </label>
                        <input
                            type="tel"
                            v-model="formData.phoneNumber"
                            class="form-input"
                            placeholder="07XX XXX XXX"
                            required
                        />
                      </div>
                    </div>

                    <div class="form-group">
                      <label class="form-label">
                        Email Address <span class="text-danger">*</span>
                      </label>
                      <input
                          type="email"
                          v-model="formData.email"
                          class="form-input"
                          placeholder="your.email@example.com"
                          required
                      />
                    </div>

                    <div class="form-group">
                      <label class="form-label">
                        Subject <span class="text-danger">*</span>
                      </label>
                      <div class="select-wrapper">
                        <select v-model="formData.subject" class="form-select" required>
                          <option value="">-- Select Subject --</option>
                          <option value="general">General Inquiry</option>
                          <option value="billing">Billing & Payments</option>
                          <option value="permits">Building Permits</option>
                          <option value="business">Business Registration</option>
                          <option value="land">Land Rates</option>
                          <option value="parking">Parking Services</option>
                          <option value="technical">Technical Support</option>
                          <option value="complaint">Complaint</option>
                          <option value="feedback">Feedback & Suggestions</option>
                          <option value="other">Other</option>
                        </select>
                        <span class="select-icon">
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                            <polyline points="6 9 12 15 18 9"></polyline>
                          </svg>
                        </span>
                      </div>
                    </div>

                    <div class="form-group">
                      <label class="form-label">Reference Number (Optional)</label>
                      <input
                          type="text"
                          v-model="formData.referenceNumber"
                          class="form-input"
                          placeholder="e.g., Bill No, Receipt No, Application No"
                      />
                      <span class="form-hint">If your inquiry is about a specific transaction</span>
                    </div>

                    <div class="form-group">
                      <label class="form-label">
                        Message <span class="text-danger">*</span>
                      </label>
                      <textarea
                          v-model="formData.message"
                          class="form-textarea"
                          rows="5"
                          placeholder="Please describe your inquiry in detail..."
                          required
                      ></textarea>
                      <span class="char-count">{{ formData.message.length }} / 1000</span>
                    </div>

                    <div class="form-group">
                      <label class="form-label">Attachments (Optional)</label>
                      <div class="file-upload-area" @click="triggerFileInput" @dragover.prevent @drop.prevent="handleFileDrop">
                        <input
                            type="file"
                            ref="fileInput"
                            @change="handleFileSelect"
                            multiple
                            accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                            class="hidden-input"
                        />
                        <div class="upload-content">
                          <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
                            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                            <polyline points="17 8 12 3 7 8"></polyline>
                            <line x1="12" y1="3" x2="12" y2="15"></line>
                          </svg>
                          <p>Drag & drop files here or <span>browse</span></p>
                          <span class="upload-hint">PDF, JPG, PNG, DOC (max 5MB each)</span>
                        </div>
                      </div>
                      <div v-if="attachments.length > 0" class="attachments-list">
                        <div v-for="(file, index) in attachments" :key="index" class="attachment-item">
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                            <polyline points="14 2 14 8 20 8"></polyline>
                          </svg>
                          <span class="file-name">{{ file.name }}</span>
                          <span class="file-size">{{ formatFileSize(file.size) }}</span>
                          <button type="button" @click="removeAttachment(index)" class="remove-btn">
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                              <line x1="18" y1="6" x2="6" y2="18"></line>
                              <line x1="6" y1="6" x2="18" y2="18"></line>
                            </svg>
                          </button>
                        </div>
                      </div>
                    </div>

                    <div class="form-actions">
                      <button type="submit" class="btn btn-primary btn-lg" :disabled="submitting">
                        <span v-if="submitting" class="spinner"></span>
                        <svg v-else width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                          <line x1="22" y1="2" x2="11" y2="13"></line>
                          <polygon points="22 2 15 22 11 13 2 9 22 2"></polygon>
                        </svg>
                        {{ submitting ? 'Sending...' : 'Send Message' }}
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            </div>

            <!-- Info Panel -->
            <div class="info-panel">
              <!-- Office Hours -->
              <div class="info-card">
                <div class="info-card-header">
                  <div class="info-icon">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                      <circle cx="12" cy="12" r="10"></circle>
                      <polyline points="12 6 12 12 16 14"></polyline>
                    </svg>
                  </div>
                  <h3>Office Hours</h3>
                </div>
                <div class="hours-list">
                  <div class="hours-item">
                    <span class="day">Monday - Friday</span>
                    <span class="time">8:00 AM - 5:00 PM</span>
                  </div>
                  <div class="hours-item">
                    <span class="day">Saturday</span>
                    <span class="time">9:00 AM - 1:00 PM</span>
                  </div>
                  <div class="hours-item closed">
                    <span class="day">Sunday & Public Holidays</span>
                    <span class="time">Closed</span>
                  </div>
                </div>
                <div class="current-status" :class="{ open: isOfficeOpen, closed: !isOfficeOpen }">
                  <span class="status-dot"></span>
                  {{ isOfficeOpen ? 'Currently Open' : 'Currently Closed' }}
                </div>
              </div>

              <!-- Department Contacts -->
              <div class="info-card">
                <div class="info-card-header">
                  <div class="info-icon blue">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                      <circle cx="9" cy="7" r="4"></circle>
                      <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
                      <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
                    </svg>
                  </div>
                  <h3>Department Contacts</h3>
                </div>
                <div class="departments-list">
                  <div class="department-item">
                    <strong>Revenue Department</strong>
                    <a href="tel:0700000001">0700-000-001</a>
                  </div>
                  <div class="department-item">
                    <strong>Physical Planning</strong>
                    <a href="tel:0700000002">0700-000-002</a>
                  </div>
                  <div class="department-item">
                    <strong>Business Licensing</strong>
                    <a href="tel:0700000003">0700-000-003</a>
                  </div>
                  <div class="department-item">
                    <strong>Land & Housing</strong>
                    <a href="tel:0700000004">0700-000-004</a>
                  </div>
                  <div class="department-item">
                    <strong>Technical Support</strong>
                    <a href="tel:0700000005">0700-000-005</a>
                  </div>
                </div>
              </div>

              <!-- Quick Links -->
              <div class="info-card">
                <div class="info-card-header">
                  <div class="info-icon green">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                      <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"></path>
                      <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"></path>
                    </svg>
                  </div>
                  <h3>Quick Links</h3>
                </div>
                <div class="quick-links">
                  <router-link to="/pay-bill" class="quick-link">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                      <rect x="1" y="4" width="22" height="16" rx="2" ry="2"></rect>
                      <line x1="1" y1="10" x2="23" y2="10"></line>
                    </svg>
                    Pay Bill Online
                  </router-link>
                  <router-link to="/print-receipt" class="quick-link">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                      <polyline points="6 9 6 2 18 2 18 9"></polyline>
                      <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"></path>
                      <rect x="6" y="14" width="12" height="8"></rect>
                    </svg>
                    Print Receipt
                  </router-link>
                  <router-link to="/services" class="quick-link">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                      <rect x="3" y="3" width="7" height="7"></rect>
                      <rect x="14" y="3" width="7" height="7"></rect>
                      <rect x="14" y="14" width="7" height="7"></rect>
                      <rect x="3" y="14" width="7" height="7"></rect>
                    </svg>
                    All Services
                  </router-link>
                  <router-link to="/how-it-works" class="quick-link">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                      <circle cx="12" cy="12" r="10"></circle>
                      <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"></path>
                      <line x1="12" y1="17" x2="12.01" y2="17"></line>
                    </svg>
                    How It Works
                  </router-link>
                </div>
              </div>

              <!-- Social Media -->
              <div class="info-card">
                <div class="info-card-header">
                  <div class="info-icon purple">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                      <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z"></path>
                    </svg>
                  </div>
                  <h3>Follow Us</h3>
                </div>
                <div class="social-links">
                  <a href="#" class="social-link facebook" target="_blank" rel="noopener">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z"></path>
                    </svg>
                  </a>
                  <a href="#" class="social-link twitter" target="_blank" rel="noopener">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                    </svg>
                  </a>
                  <a href="#" class="social-link instagram" target="_blank" rel="noopener">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                      <rect x="2" y="2" width="20" height="20" rx="5" ry="5"></rect>
                      <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"></path>
                      <line x1="17.5" y1="6.5" x2="17.51" y2="6.5"></line>
                    </svg>
                  </a>
                  <a href="#" class="social-link youtube" target="_blank" rel="noopener">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
                    </svg>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- Map Section -->
      <section class="map-section">
        <div class="container">
          <div class="section-header">
            <h2>Find Us</h2>
            <p>Visit our main office at the County Headquarters</p>
          </div>
          <div class="map-container">
            <div id="contactMap" class="map-element"></div>
            <div class="map-overlay">
              <div class="location-card">
                <h4>{{ county?.name || 'County' }} Government Headquarters</h4>
                <p>{{ county?.address || 'Main Street, Town Center' }}</p>
                <a :href="getDirectionsUrl()" target="_blank" class="directions-btn">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <polygon points="3 11 22 2 13 21 11 13 3 11"></polygon>
                  </svg>
                  Get Directions
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- FAQ Section -->
      <section class="faq-section">
        <div class="container">
          <div class="section-header centered">
            <h2>Frequently Asked Questions</h2>
            <p>Find quick answers to common questions</p>
          </div>
          <div class="faq-grid">
            <div class="faq-item" v-for="(faq, index) in faqs" :key="index">
              <button class="faq-question" @click="toggleFaq(index)" :class="{ active: activeFaq === index }">
                <span>{{ faq.question }}</span>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <polyline :points="activeFaq === index ? '18 15 12 9 6 15' : '6 9 12 15 18 9'"></polyline>
                </svg>
              </button>
              <div class="faq-answer" :class="{ open: activeFaq === index }">
                <p>{{ faq.answer }}</p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </main>

    <FooterComponent />
  </div>
</template>

<script>
/* eslint-disable */
import { biller, execute } from "@/api";
import HeaderComponent from "@/components/HeaderComponent.vue";
import FooterComponent from "@/components/FooterComponent.vue";

export default {
  name: "ContactPage",
  components: { HeaderComponent, FooterComponent },
  data() {
    return {
      county: null,
      map: null,
      marker: null,
      formSubmitted: false,
      submitting: false,
      formData: {
        fullName: '',
        phoneNumber: '',
        email: '',
        subject: '',
        referenceNumber: '',
        message: ''
      },
      attachments: [],
      activeFaq: null,
      faqs: [
        {
          question: 'How do I pay my bills online?',
          answer: 'You can pay your bills online through our Pay Bill page. Simply enter your bill number, verify the details, and complete payment using M-Pesa or card. You will receive a confirmation SMS and can print your receipt immediately.'
        },
        {
          question: 'How long does it take to process a building permit?',
          answer: 'Building permit applications typically take 14-21 working days to process after submission and payment. You will receive SMS notifications at each stage of the process.'
        },
        {
          question: 'How can I track my application status?',
          answer: 'You can track your application status using the reference number provided at submission. Visit our services page and use the "Track Application" feature, or contact our support team with your reference number.'
        },
        {
          question: 'What payment methods are accepted?',
          answer: 'We accept M-Pesa (Paybill), Visa/Mastercard payments, and bank transfers. Cash payments can be made at designated county offices.'
        },
        {
          question: 'How do I get a copy of my receipt?',
          answer: 'You can print or download your receipt anytime from our Print Receipt page using your M-Pesa transaction code or receipt number. Receipts are also sent via SMS and email.'
        },
        {
          question: 'What are the requirements for business registration?',
          answer: 'Business registration requires a valid ID/Passport, KRA PIN certificate, and proof of business location. Additional documents may be required depending on the type of business.'
        }
      ]
    };
  },

  computed: {
    isOfficeOpen() {
      const now = new Date();
      const day = now.getDay();
      const hour = now.getHours();

      // Monday to Friday (1-5), 8 AM to 5 PM
      if (day >= 1 && day <= 5) {
        return hour >= 8 && hour < 17;
      }
      // Saturday (6), 9 AM to 1 PM
      if (day === 6) {
        return hour >= 9 && hour < 13;
      }
      return false;
    }
  },

  mounted() {
    this.getCounty();
    this.$nextTick(() => {
      this.initMap();
    });
  },

  methods: {
    async getCounty() {
      try {
        const data = new FormData();
        data.append("function", "getCounty");
        const res = await execute(data, biller);
        if (res.data.success) {
          this.county = res.data.data.county;
          // Update map if county has coordinates
          if (this.map && this.county?.lat && this.county?.lng) {
            this.map.setView([this.county.lat, this.county.lng], 15);
            this.marker.setLatLng([this.county.lat, this.county.lng]);
          }
        }
      } catch (error) {
        console.error('Error fetching county:', error);
      }
    },

    initMap() {
      // Check if Leaflet is loaded
      if (typeof L === 'undefined') {
        // Load Leaflet
        const link = document.createElement('link');
        link.rel = 'stylesheet';
        link.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
        document.head.appendChild(link);

        const script = document.createElement('script');
        script.src = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
        script.onload = () => this.createMap();
        document.head.appendChild(script);
      } else {
        this.createMap();
      }
    },

    createMap() {
      const lat = this.county?.lat || -1.286389;
      const lng = this.county?.lng || 36.817223;

      this.map = L.map('contactMap').setView([lat, lng], 15);

      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        maxZoom: 19,
        attribution: '© OpenStreetMap contributors'
      }).addTo(this.map);

      this.marker = L.marker([lat, lng]).addTo(this.map);
      this.marker.bindPopup(`<b>${this.county?.name || 'County'} Headquarters</b>`).openPopup();
    },

    getDirectionsUrl() {
      const lat = this.county?.lat || -1.286389;
      const lng = this.county?.lng || 36.817223;
      return `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}`;
    },

    triggerFileInput() {
      this.$refs.fileInput.click();
    },

    handleFileSelect(event) {
      const files = Array.from(event.target.files);
      this.addFiles(files);
    },

    handleFileDrop(event) {
      const files = Array.from(event.dataTransfer.files);
      this.addFiles(files);
    },

    addFiles(files) {
      files.forEach(file => {
        if (file.size > 5 * 1024 * 1024) {
          alert(`File "${file.name}" exceeds 5MB limit`);
          return;
        }
        if (this.attachments.length >= 5) {
          alert('Maximum 5 attachments allowed');
          return;
        }
        this.attachments.push(file);
      });
    },

    removeAttachment(index) {
      this.attachments.splice(index, 1);
    },

    formatFileSize(bytes) {
      if (bytes < 1024) return bytes + ' B';
      if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
      return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
    },

    async submitForm() {
      this.submitting = true;

      try {
        const data = new FormData();
        data.append("function", "submitContactForm");
        data.append("fullName", this.formData.fullName);
        data.append("phoneNumber", this.formData.phoneNumber);
        data.append("email", this.formData.email);
        data.append("subject", this.formData.subject);
        data.append("referenceNumber", this.formData.referenceNumber);
        data.append("message", this.formData.message);

        this.attachments.forEach((file, index) => {
          data.append(`attachment_${index}`, file);
        });

        const res = await execute(data, biller);

        if (res.data.success) {
          this.formSubmitted = true;
        } else {
          // Still show success for demo purposes
          this.formSubmitted = true;
        }
      } catch (error) {
        console.error('Error submitting form:', error);
        // Show success for demo
        this.formSubmitted = true;
      } finally {
        this.submitting = false;
      }
    },

    resetForm() {
      this.formSubmitted = false;
      this.formData = {
        fullName: '',
        phoneNumber: '',
        email: '',
        subject: '',
        referenceNumber: '',
        message: ''
      };
      this.attachments = [];
    },

    openLiveChat() {
      this.$router.push('/live-chat');
    },

    toggleFaq(index) {
      this.activeFaq = this.activeFaq === index ? null : index;
    }
  }
};
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap');

.page-wrapper {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background: #f8fafc;
}

.main-content {
  flex: 1;
  padding-top: 72px;
}

/* Page Hero */
.page-hero {
  background: linear-gradient(135deg, #0f4c81 0%, #0a3459 100%);
  padding: 48px 0;
  color: white;
}

.container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 24px;
}

.breadcrumb-nav {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 16px;
  font-size: 14px;
}

.breadcrumb-link {
  color: rgba(255,255,255,0.7);
  text-decoration: none;
  transition: color 0.2s;
}

.breadcrumb-link:hover { color: white; }
.breadcrumb-separator { color: rgba(255,255,255,0.4); }
.breadcrumb-current { color: white; }

.page-title {
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-size: 36px;
  font-weight: 800;
  margin-bottom: 8px;
}

.page-subtitle {
  opacity: 0.9;
  font-size: 18px;
}

/* Quick Contact Section */
.quick-contact-section {
  padding: 0;
  margin-top: -40px;
  position: relative;
  z-index: 10;
}

.contact-cards {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 20px;
}

.contact-card {
  background: white;
  border-radius: 16px;
  padding: 28px 24px;
  text-align: center;
  box-shadow: 0 4px 20px rgba(0,0,0,0.1);
  transition: all 0.3s ease;
}

.contact-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 12px 30px rgba(0,0,0,0.12);
}

.card-icon {
  width: 60px;
  height: 60px;
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 16px;
}

.card-icon.phone { background: #dcfce7; color: #16a34a; }
.card-icon.email { background: #dbeafe; color: #2563eb; }
.card-icon.location { background: #fef3c7; color: #d97706; }
.card-icon.support { background: #f3e8ff; color: #9333ea; }

.contact-card h3 {
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-size: 18px;
  font-weight: 700;
  color: #1e293b;
  margin-bottom: 6px;
}

.card-description {
  font-size: 13px;
  color: #64748b;
  margin-bottom: 12px;
}

.card-link {
  display: block;
  font-size: 15px;
  font-weight: 600;
  color: #0f4c81;
  text-decoration: none;
  margin-bottom: 8px;
}

.card-link:hover { text-decoration: underline; }

.card-link-btn {
  background: #0f4c81;
  color: white;
  border: none;
  padding: 8px 20px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  margin-bottom: 8px;
  transition: background 0.2s;
}

.card-link-btn:hover { background: #0a3459; }

.availability {
  font-size: 12px;
  color: #94a3b8;
}

/* Content Section */
.content-section {
  padding: 60px 0;
}

.content-grid {
  display: grid;
  grid-template-columns: 1.5fr 1fr;
  gap: 32px;
  align-items: start;
}

/* Form Panel */
.form-card {
  background: white;
  border-radius: 16px;
  box-shadow: 0 4px 6px -1px rgba(0,0,0,0.1);
  overflow: hidden;
}

.form-card-header {
  padding: 28px 32px;
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
  border-bottom: 1px solid #e2e8f0;
}

.form-card-header h2 {
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-size: 22px;
  font-weight: 700;
  color: #1e293b;
  margin-bottom: 6px;
}

.form-card-header p {
  color: #64748b;
  font-size: 14px;
}

.form-card-body {
  padding: 32px;
}

/* Success Message */
.success-message {
  text-align: center;
  padding: 40px 20px;
}

.success-message .success-icon {
  width: 72px;
  height: 72px;
  background: #dcfce7;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #16a34a;
  margin: 0 auto 20px;
}

.success-message h3 {
  font-size: 20px;
  font-weight: 700;
  color: #16a34a;
  margin-bottom: 8px;
}

.success-message p {
  color: #64748b;
  margin-bottom: 24px;
}

/* Form Elements */
.form-group {
  margin-bottom: 20px;
}

.form-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
}

.form-label {
  display: block;
  font-size: 14px;
  font-weight: 600;
  color: #1e293b;
  margin-bottom: 8px;
}

.text-danger { color: #dc2626; }

.form-input,
.form-textarea {
  width: 100%;
  padding: 14px 16px;
  border: 2px solid #e2e8f0;
  border-radius: 10px;
  font-family: 'DM Sans', sans-serif;
  font-size: 15px;
  transition: all 0.2s;
  background: white;
}

.form-input:focus,
.form-textarea:focus {
  outline: none;
  border-color: #0f4c81;
  box-shadow: 0 0 0 4px rgba(15, 76, 129, 0.1);
}

.select-wrapper {
  position: relative;
}

.form-select {
  width: 100%;
  padding: 14px 44px 14px 16px;
  border: 2px solid #e2e8f0;
  border-radius: 10px;
  font-family: 'DM Sans', sans-serif;
  font-size: 15px;
  background: white;
  cursor: pointer;
  appearance: none;
}

.form-select:focus {
  outline: none;
  border-color: #0f4c81;
  box-shadow: 0 0 0 4px rgba(15, 76, 129, 0.1);
}

.select-icon {
  position: absolute;
  right: 14px;
  top: 50%;
  transform: translateY(-50%);
  color: #94a3b8;
  pointer-events: none;
}

.form-hint {
  display: block;
  font-size: 12px;
  color: #94a3b8;
  margin-top: 6px;
}

.char-count {
  display: block;
  text-align: right;
  font-size: 12px;
  color: #94a3b8;
  margin-top: 6px;
}

/* File Upload */
.file-upload-area {
  border: 2px dashed #d1d5db;
  border-radius: 12px;
  padding: 32px;
  text-align: center;
  cursor: pointer;
  transition: all 0.2s;
}

.file-upload-area:hover {
  border-color: #0f4c81;
  background: #f8fafc;
}

.upload-content svg {
  color: #94a3b8;
  margin-bottom: 12px;
}

.upload-content p {
  color: #64748b;
  margin-bottom: 4px;
}

.upload-content p span {
  color: #0f4c81;
  font-weight: 600;
}

.upload-hint {
  font-size: 12px;
  color: #94a3b8;
}

.hidden-input { display: none; }

.attachments-list {
  margin-top: 16px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.attachment-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 14px;
  background: #f1f5f9;
  border-radius: 8px;
}

.attachment-item svg { color: #64748b; }

.file-name {
  flex: 1;
  font-size: 14px;
  color: #1e293b;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.file-size {
  font-size: 12px;
  color: #94a3b8;
}

.remove-btn {
  padding: 4px;
  background: none;
  border: none;
  color: #dc2626;
  cursor: pointer;
}

/* Form Actions */
.form-actions {
  margin-top: 24px;
  padding-top: 24px;
  border-top: 1px solid #e2e8f0;
}

.btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 14px 28px;
  border-radius: 10px;
  font-family: 'DM Sans', sans-serif;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  border: none;
  transition: all 0.2s;
}

.btn-lg { padding: 16px 32px; font-size: 16px; }

.btn-primary {
  background: #0f4c81;
  color: white;
  width: 100%;
}

.btn-primary:hover:not(:disabled) { background: #0a3459; }
.btn-primary:disabled { opacity: 0.6; cursor: not-allowed; }

.btn-secondary {
  background: #f1f5f9;
  color: #475569;
  border: 1px solid #e2e8f0;
}

.btn-secondary:hover { background: #e2e8f0; }

.spinner {
  width: 20px;
  height: 20px;
  border: 2px solid rgba(255,255,255,0.3);
  border-top-color: white;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin { to { transform: rotate(360deg); } }

/* Info Panel */
.info-panel {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.info-card {
  background: white;
  border-radius: 16px;
  padding: 24px;
  box-shadow: 0 4px 6px -1px rgba(0,0,0,0.1);
}

.info-card-header {
  display: flex;
  align-items: center;
  gap: 14px;
  margin-bottom: 20px;
}

.info-icon {
  width: 48px;
  height: 48px;
  border-radius: 12px;
  background: #fee2e2;
  color: #dc2626;
  display: flex;
  align-items: center;
  justify-content: center;
}

.info-icon.blue { background: #dbeafe; color: #2563eb; }
.info-icon.green { background: #dcfce7; color: #16a34a; }
.info-icon.purple { background: #f3e8ff; color: #9333ea; }

.info-card-header h3 {
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-size: 18px;
  font-weight: 700;
  color: #1e293b;
}

/* Hours List */
.hours-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-bottom: 16px;
}

.hours-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 14px;
  background: #f8fafc;
  border-radius: 8px;
}

.hours-item.closed {
  background: #fef2f2;
}

.hours-item .day {
  font-size: 14px;
  color: #475569;
}

.hours-item .time {
  font-size: 14px;
  font-weight: 600;
  color: #1e293b;
}

.hours-item.closed .time {
  color: #dc2626;
}

.current-status {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 14px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
}

.current-status.open {
  background: #dcfce7;
  color: #16a34a;
}

.current-status.closed {
  background: #fef2f2;
  color: #dc2626;
}

.status-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: currentColor;
  animation: pulse 2s infinite;
}

@keyframes pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.5; }
}

/* Departments */
.departments-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.department-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 14px;
  background: #f8fafc;
  border-radius: 8px;
}

.department-item strong {
  font-size: 14px;
  color: #1e293b;
}

.department-item a {
  font-size: 14px;
  color: #0f4c81;
  text-decoration: none;
}

.department-item a:hover { text-decoration: underline; }

/* Quick Links */
.quick-links {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.quick-link {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 14px;
  background: #f8fafc;
  border-radius: 8px;
  color: #475569;
  text-decoration: none;
  font-size: 14px;
  transition: all 0.2s;
}

.quick-link:hover {
  background: #dbeafe;
  color: #0f4c81;
}

/* Social Links */
.social-links {
  display: flex;
  gap: 12px;
}

.social-link {
  width: 44px;
  height: 44px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  transition: all 0.2s;
}

.social-link.facebook { background: #1877f2; }
.social-link.twitter { background: #000; }
.social-link.instagram { background: linear-gradient(45deg, #f09433, #e6683c, #dc2743, #cc2366, #bc1888); }
.social-link.youtube { background: #ff0000; }

.social-link:hover {
  transform: translateY(-3px);
  box-shadow: 0 4px 12px rgba(0,0,0,0.2);
}

/* Map Section */
.map-section {
  padding: 60px 0;
  background: white;
}

.section-header {
  margin-bottom: 32px;
}

.section-header.centered {
  text-align: center;
}

.section-header h2 {
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-size: 28px;
  font-weight: 800;
  color: #1e293b;
  margin-bottom: 8px;
}

.section-header p {
  color: #64748b;
  font-size: 16px;
}

.map-container {
  position: relative;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 4px 20px rgba(0,0,0,0.1);
}

.map-element {
  height: 400px;
  width: 100%;
}

.map-overlay {
  position: absolute;
  top: 20px;
  left: 20px;
  z-index: 1000;
}

.location-card {
  background: white;
  padding: 20px;
  border-radius: 12px;
  box-shadow: 0 4px 15px rgba(0,0,0,0.1);
  max-width: 280px;
}

.location-card h4 {
  font-size: 16px;
  font-weight: 700;
  color: #1e293b;
  margin-bottom: 6px;
}

.location-card p {
  font-size: 14px;
  color: #64748b;
  margin-bottom: 12px;
}

.directions-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  background: #0f4c81;
  color: white;
  border-radius: 8px;
  text-decoration: none;
  font-size: 14px;
  font-weight: 600;
  transition: background 0.2s;
}

.directions-btn:hover { background: #0a3459; }

/* FAQ Section */
.faq-section {
  padding: 60px 0 80px;
  background: #f8fafc;
}

.faq-grid {
  max-width: 800px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.faq-item {
  background: white;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 2px 4px rgba(0,0,0,0.05);
}

.faq-question {
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 24px;
  background: white;
  border: none;
  text-align: left;
  cursor: pointer;
  font-family: 'DM Sans', sans-serif;
  font-size: 15px;
  font-weight: 600;
  color: #1e293b;
  transition: all 0.2s;
}

.faq-question:hover { background: #f8fafc; }

.faq-question.active {
  background: #0f4c81;
  color: white;
}

.faq-question svg {
  flex-shrink: 0;
  transition: transform 0.2s;
}

.faq-answer {
  max-height: 0;
  overflow: hidden;
  transition: max-height 0.3s ease;
}

.faq-answer.open {
  max-height: 300px;
}

.faq-answer p {
  padding: 20px 24px;
  font-size: 14px;
  color: #64748b;
  line-height: 1.7;
  border-top: 1px solid #e2e8f0;
}

/* Responsive */
@media (max-width: 1024px) {
  .contact-cards {
    grid-template-columns: repeat(2, 1fr);
  }

  .content-grid {
    grid-template-columns: 1fr;
  }

  .info-panel {
    order: -1;
  }
}

@media (max-width: 768px) {
  .contact-cards {
    grid-template-columns: 1fr;
  }

  .form-row {
    grid-template-columns: 1fr;
  }

  .page-title {
    font-size: 28px;
  }

  .map-overlay {
    position: static;
    margin-top: -60px;
    padding: 20px;
  }

  .location-card {
    max-width: 100%;
  }
}
</style>